import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef, useCallback } from 'react';

export type EraserType = 'brush' | 'stroke';

export interface DrawingCanvasRef {
  clear: () => void;
  undo: () => void;
  setColor: (color: string) => void;
  setStrokeWidth: (width: number) => void;
  setEraserMode: (isEraser: boolean, type?: EraserType) => void;
}

interface DrawingCanvasProps {
  isDrawingMode: boolean;
  className?: string;
  defaultColor?: string;
  defaultStrokeWidth?: number;
}

interface StrokeInfo {
  color: string;
  width: number;
  isEraser: boolean;
  points: { x: number; y: number }[];
}

export const DrawingCanvas = forwardRef<DrawingCanvasRef, DrawingCanvasProps>(
  ({ isDrawingMode, className = "", defaultColor = "#ef4444", defaultStrokeWidth = 3 }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const cursorCanvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [isDrawing, setIsDrawing] = useState(false);
    
    // Drawing state
    const colorRef = useRef(defaultColor);
    const strokeWidthRef = useRef(defaultStrokeWidth);
    const isEraserModeRef = useRef(false);
    const eraserTypeRef = useRef<EraserType>('brush');
    const [, forceUpdate] = useState(0);
    const strokesRef = useRef<StrokeInfo[]>([]);
    const currentStrokeRef = useRef<StrokeInfo | null>(null);

    // Undo history: tracks add/remove actions for full undo support
    const undoStackRef = useRef<{ type: 'add' | 'remove'; stroke: StrokeInfo; index: number }[]>([]);

    // Cursor state
    const cursorPosRef = useRef<{ x: number; y: number } | null>(null);
    const isHoveringRef = useRef(false);
    const rafRef = useRef<number>(0);

    // Provide methods to parent
    useImperativeHandle(ref, () => ({
      clear: () => {
        strokesRef.current = [];
        undoStackRef.current = [];
        redrawCanvas();
      },
      undo: () => {
        const action = undoStackRef.current.pop();
        if (!action) return;
        if (action.type === 'add') {
          // Remove the last added stroke
          const idx = strokesRef.current.lastIndexOf(action.stroke);
          if (idx !== -1) strokesRef.current.splice(idx, 1);
        } else {
          // Restore a stroke-erased stroke at its original position
          strokesRef.current.splice(Math.min(action.index, strokesRef.current.length), 0, action.stroke);
        }
        redrawCanvas();
      },
      setColor: (newColor: string) => {
        colorRef.current = newColor;
        isEraserModeRef.current = false;
        forceUpdate(n => n + 1);
      },
      setStrokeWidth: (width: number) => {
        strokeWidthRef.current = width;
        forceUpdate(n => n + 1);
      },
      setEraserMode: (isEraser: boolean, type: EraserType = 'brush') => {
        isEraserModeRef.current = isEraser;
        eraserTypeRef.current = type;
        forceUpdate(n => n + 1);
      },
    }));

    // Handle canvas resizing
    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          const canvas = canvasRef.current;
          const cursorCanvas = cursorCanvasRef.current;
          if (!canvas) continue;

          canvas.width = entry.contentRect.width;
          canvas.height = entry.contentRect.height;

          if (cursorCanvas) {
            cursorCanvas.width = entry.contentRect.width;
            cursorCanvas.height = entry.contentRect.height;
          }

          redrawCanvas();
        }
      });

      resizeObserver.observe(container);

      return () => {
        resizeObserver.disconnect();
      };
    }, []);

    const redrawCanvas = useCallback(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      strokesRef.current.forEach(stroke => {
        if (stroke.points.length === 0) return;
        
        ctx.globalCompositeOperation = stroke.isEraser ? 'destination-out' : 'source-over';
        ctx.strokeStyle = stroke.color;
        ctx.lineWidth = stroke.width;
        ctx.beginPath();
        
        ctx.moveTo(stroke.points[0].x, stroke.points[0].y);
        for (let i = 1; i < stroke.points.length; i++) {
          ctx.lineTo(stroke.points[i].x, stroke.points[i].y);
        }
        ctx.stroke();
      });

      ctx.globalCompositeOperation = 'source-over';
    }, []);

    // Draw cursor circle on the overlay canvas
    const drawCursor = useCallback(() => {
      const cursorCanvas = cursorCanvasRef.current;
      if (!cursorCanvas) return;
      const ctx = cursorCanvas.getContext('2d');
      if (!ctx) return;

      ctx.clearRect(0, 0, cursorCanvas.width, cursorCanvas.height);

      if (!isHoveringRef.current || !cursorPosRef.current || !isDrawingMode) return;

      const { x, y } = cursorPosRef.current;
      const radius = strokeWidthRef.current / 2;
      const isEraser = isEraserModeRef.current;
      const isStrokeEraser = isEraser && eraserTypeRef.current === 'stroke';

      ctx.beginPath();
      ctx.arc(x, y, Math.max(radius, 2), 0, Math.PI * 2);
      
      if (isStrokeEraser) {
        // Stroke eraser: dashed red circle
        ctx.setLineDash([4, 4]);
        ctx.strokeStyle = 'rgba(239, 68, 68, 0.8)';
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.setLineDash([]);
        // Small crosshair in center
        const ch = 6;
        ctx.strokeStyle = 'rgba(239, 68, 68, 0.6)';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(x - ch, y); ctx.lineTo(x + ch, y);
        ctx.moveTo(x, y - ch); ctx.lineTo(x, y + ch);
        ctx.stroke();
      } else if (isEraser) {
        // Brush eraser: solid circle outline
        ctx.strokeStyle = 'rgba(150, 150, 150, 0.7)';
        ctx.lineWidth = 1.5;
        ctx.stroke();
        // Fill with semi-transparent
        ctx.fillStyle = 'rgba(200, 200, 200, 0.15)';
        ctx.fill();
      } else {
        // Drawing: colored circle outline
        ctx.strokeStyle = colorRef.current;
        ctx.lineWidth = 1.5;
        ctx.globalAlpha = 0.6;
        ctx.stroke();
        ctx.globalAlpha = 1;
      }
    }, [isDrawingMode]);

    // Point-to-segment distance for stroke hit testing
    const pointToSegmentDist = (px: number, py: number, ax: number, ay: number, bx: number, by: number) => {
      const dx = bx - ax, dy = by - ay;
      const lenSq = dx * dx + dy * dy;
      if (lenSq === 0) return Math.hypot(px - ax, py - ay);
      let t = ((px - ax) * dx + (py - ay) * dy) / lenSq;
      t = Math.max(0, Math.min(1, t));
      return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
    };

    // Find and remove stroke at a given point
    const eraseStrokeAt = (x: number, y: number) => {
      const threshold = Math.max(strokeWidthRef.current, 8);
      // Iterate in reverse so we remove the topmost stroke first
      for (let i = strokesRef.current.length - 1; i >= 0; i--) {
        const stroke = strokesRef.current[i];
        if (stroke.isEraser) continue; // Don't erase eraser strokes
        
        for (let j = 0; j < stroke.points.length; j++) {
          const p = stroke.points[j];
          // Check single point
          if (Math.hypot(x - p.x, y - p.y) < threshold + stroke.width / 2) {
            strokesRef.current.splice(i, 1);
            undoStackRef.current.push({ type: 'remove', stroke, index: i });
            redrawCanvas();
            return true;
          }
          // Check segment to next point
          if (j < stroke.points.length - 1) {
            const p2 = stroke.points[j + 1];
            if (pointToSegmentDist(x, y, p.x, p.y, p2.x, p2.y) < threshold + stroke.width / 2) {
              strokesRef.current.splice(i, 1);
              undoStackRef.current.push({ type: 'remove', stroke, index: i });
              redrawCanvas();
              return true;
            }
          }
        }
      }
      return false;
    };

    const getCoordinates = (e: React.PointerEvent<HTMLCanvasElement> | PointerEvent) => {
      const canvas = canvasRef.current;
      if (!canvas) return { x: 0, y: 0 };
      const rect = canvas.getBoundingClientRect();
      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      };
    };

    const startDrawing = (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (!isDrawingMode) return;
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      
      const { x, y } = getCoordinates(e);
      setIsDrawing(true);

      // Stroke eraser mode: just try to erase at the point
      if (isEraserModeRef.current && eraserTypeRef.current === 'stroke') {
        eraseStrokeAt(x, y);
        return;
      }
      
      currentStrokeRef.current = {
        color: colorRef.current,
        width: strokeWidthRef.current,
        isEraser: isEraserModeRef.current,
        points: [{ x, y }]
      };
      strokesRef.current.push(currentStrokeRef.current);
      undoStackRef.current.push({ type: 'add', stroke: currentStrokeRef.current, index: strokesRef.current.length - 1 });
      
      const ctx = canvasRef.current?.getContext('2d');
      if (ctx) {
        ctx.globalCompositeOperation = isEraserModeRef.current ? 'destination-out' : 'source-over';
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = colorRef.current;
        ctx.lineWidth = strokeWidthRef.current;
        ctx.beginPath();
        ctx.moveTo(x, y);
      }
    };

    const draw = (e: React.PointerEvent<HTMLCanvasElement>) => {
      const { x, y } = getCoordinates(e);
      
      // Update cursor position and redraw cursor
      cursorPosRef.current = { x, y };
      isHoveringRef.current = true;
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(drawCursor);

      if (!isDrawing || !isDrawingMode) return;

      // Stroke eraser: continuously erase strokes as pointer moves
      if (isEraserModeRef.current && eraserTypeRef.current === 'stroke') {
        eraseStrokeAt(x, y);
        return;
      }

      if (!currentStrokeRef.current) return;
      
      currentStrokeRef.current.points.push({ x, y });
      
      const ctx = canvasRef.current?.getContext('2d');
      if (ctx) {
        ctx.lineTo(x, y);
        ctx.stroke();
      }
    };

    const stopDrawing = (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (!isDrawing) return;
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
      setIsDrawing(false);
      currentStrokeRef.current = null;
    };

    const handlePointerEnter = () => {
      isHoveringRef.current = true;
    };

    const handlePointerLeave = () => {
      isHoveringRef.current = false;
      cursorPosRef.current = null;
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(drawCursor);
    };

    // Cleanup RAF on unmount
    useEffect(() => {
      return () => cancelAnimationFrame(rafRef.current);
    }, []);

    return (
      <div 
        ref={containerRef} 
        className={`absolute inset-0 z-50 ${!isDrawingMode ? 'pointer-events-none' : 'touch-none'} ${className}`}
        style={{ cursor: isDrawingMode ? 'none' : undefined }}
      >
        <canvas
          ref={canvasRef}
          className="w-full h-full block absolute inset-0"
          style={{ cursor: isDrawingMode ? 'none' : undefined }}
          onPointerDown={startDrawing}
          onPointerMove={draw}
          onPointerUp={stopDrawing}
          onPointerCancel={stopDrawing}
          onPointerEnter={handlePointerEnter}
          onPointerLeave={handlePointerLeave}
        />
        {/* Cursor overlay canvas - renders the brush size circle */}
        <canvas
          ref={cursorCanvasRef}
          className="w-full h-full block absolute inset-0 pointer-events-none"
          style={{ cursor: 'none' }}
        />
      </div>
    );
  }
);

DrawingCanvas.displayName = 'DrawingCanvas';
